import { useState, useRef, useEffect } from 'react';
import { Menu, Search, Bell, ClipboardCheck, LogOut, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth, useUser } from '@/hooks/useAuth';
import ApprovalBadge from '@/components/Approvals/ApprovalBadge';
import Breadcrumb from './Breadcrumb';

interface HeaderProps {
  onMenuToggle: () => void;
}

export default function Header({ onMenuToggle }: HeaderProps) {
  const { logout } = useAuth();
  const { name, role } = useUser();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close user menu on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Generate user initials from name
  const initials = name
    ? name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : '??';

  // Format role for display
  const roleLabel = role
    ? role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : '';

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200 bg-white/80 px-4 backdrop-blur-md lg:px-6">
      {/* Left side: hamburger + breadcrumb */}
      <div className="flex items-center gap-3">
        <button
          onClick={onMenuToggle}
          className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 lg:hidden"
          aria-label="Toggle navigation menu"
        >
          <Menu size={20} />
        </button>
        <Breadcrumb />
      </div>

      {/* Right side: search, notifications, user */}
      <div className="flex items-center gap-2">
        {/* Global search button */}
        <button
          className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-50 transition-colors"
          aria-label="Open search"
        >
          <Search size={16} />
          <span className="hidden md:inline">Search</span>
          <kbd className="hidden rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-400 md:inline">
            ⌘K
          </kbd>
        </button>

        {/* Pending approvals badge */}
        <Link
          to="/approvals"
          className="relative rounded-lg p-2 text-slate-600 hover:bg-slate-100 transition-colors"
          aria-label="Pending approvals"
        >
          <ClipboardCheck size={20} />
          <ApprovalBadge />
        </Link>

        {/* Notification bell */}
        <button
          className="relative rounded-lg p-2 text-slate-600 hover:bg-slate-100 transition-colors"
          aria-label="Notifications"
        >
          <Bell size={20} />
          {/* Unread indicator dot */}
          <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-indigo-500 animate-pulse" />
        </button>

        {/* User avatar dropdown */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setUserMenuOpen((prev) => !prev)}
            className="flex items-center gap-2 rounded-lg p-1.5 hover:bg-slate-100 transition-colors"
            aria-label="User menu"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600 text-xs font-semibold text-white">
              {initials}
            </div>
            <ChevronDown size={14} className="text-slate-400" />
          </button>

          {/* Dropdown menu */}
          {userMenuOpen && (
            <div className="absolute right-0 top-12 z-50 w-56 rounded-xl border border-slate-200 bg-white p-2 shadow-lg">
              <div className="border-b border-slate-100 px-3 py-2">
                <p className="text-sm font-medium text-slate-900">{name || 'User'}</p>
                {roleLabel && (
                  <span className="mt-0.5 inline-block rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700">
                    {roleLabel}
                  </span>
                )}
              </div>
              <button
                onClick={() => {
                  setUserMenuOpen(false);
                  logout();
                }}
                className="mt-1 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
              >
                <LogOut size={16} />
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
